/**
 * Body of the pick view below the context banner: loading / failed / empty
 * states, or the column grid with the expand toggle, "Späteres Datum wählen",
 * the booking note and the optional phone line.
 */
import { FC, ReactNode } from "react"
import {
  ActivityIndicator,
  Pressable,
  PressableStateCallbackType,
  TextStyle,
  View,
  ViewStyle,
} from "react-native"
import Icon from "react-native-vector-icons/MaterialIcons"

import { Text } from "@/components/Text"
import { colors, spacing } from "@/theme"

import { CallLine, type CallLineProps } from "./CallLine"
import { MoreToggle } from "./MoreToggle"
import type { SlotGridView } from "./rescheduleSlotModel"
import { SlotGrid } from "./SlotGrid"
import { font, tokens } from "./tokens"

export interface PickBodyProps extends CallLineProps {
  loading: boolean
  failed: boolean
  noDays: boolean
  view: SlotGridView
  expanded: boolean
  onToggleExpanded: () => void
  onPrev: () => void
  onNext: () => void
  onSelect: (date: string, time: string) => void
  onRetryDay: (date: string) => void
  onOpenCalendar: () => void
}

type HoverState = PressableStateCallbackType & { hovered?: boolean }

export const PickBody: FC<PickBodyProps> = (props) => {
  const { loading, failed, noDays, view, expanded } = props
  if (loading) {
    return (
      <StateBox>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={$stateText}>Öffnungszeiten werden geladen...</Text>
      </StateBox>
    )
  }
  if (failed) {
    return (
      <StateBox>
        <Text style={$stateText}>
          Termine konnten nicht geladen werden. Bitte später erneut versuchen.
        </Text>
      </StateBox>
    )
  }
  if (noDays) {
    return (
      <StateBox>
        <Text style={$stateText}>Aktuell sind keine freien Tage verfügbar.</Text>
      </StateBox>
    )
  }
  return (
    <View style={$body}>
      <SlotGrid
        view={view}
        expanded={expanded}
        onPrev={props.onPrev}
        onNext={props.onNext}
        onSelect={props.onSelect}
        onRetryDay={props.onRetryDay}
      />
      {view.hasOverflow && (
        <MoreToggle
          expanded={expanded}
          hiddenTotal={view.hiddenTotal}
          onToggle={props.onToggleExpanded}
        />
      )}
      <Pressable
        accessibilityRole="button"
        onPress={props.onOpenCalendar}
        testID="later-date-link"
        style={$later}
      >
        {(state) => {
          const color = (state as HoverState).hovered ? tokens.red : tokens.ink
          return (
            <>
              <Icon name="event" size={20} color={color} />
              <Text style={[$laterText, { color }]}>Späteres Datum wählen</Text>
            </>
          )
        }}
      </Pressable>
      <Text style={$note}>Heute online buchbar bis 30 Minuten vor dem Termin.</Text>
      <CallLine phone={props.phone} hours={props.hours} />
    </View>
  )
}

const StateBox: FC<{ children: ReactNode }> = ({ children }) => (
  <View style={$stateBox}>{children}</View>
)

const $body: ViewStyle = { gap: 16 }
const $later: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
  minHeight: 44,
  alignSelf: "flex-start",
}
const $laterText: TextStyle = {
  fontSize: 15,
  fontFamily: font.bold,
  textDecorationLine: "underline",
}
const $note: TextStyle = {
  marginTop: -8,
  fontSize: 13,
  lineHeight: 18,
  fontFamily: font.regular,
  color: tokens.ink70,
}
const $stateBox: ViewStyle = {
  alignItems: "center",
  justifyContent: "center",
  paddingVertical: spacing.xl,
}
const $stateText: TextStyle = {
  fontSize: 13,
  color: colors.textDim,
  fontWeight: "500",
  marginTop: spacing.sm,
  textAlign: "center",
}
